import { createContext, ReactNode, useEffect, useState } from "react";
import { UserType } from "../types";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../hooks/useAuth";
import LoadingSplash from "../components/LoadingSplash";

// Turns a failed /login/db_check into a message naming the layer that actually broke.
//
// `fetchApi` puts the HTTP status on the thrown error as `.status`; a fetch that never got a
// response at all (backend not running, wrong VITE_API_URL, CORS rejection) throws a TypeError
// with no `.status`. Only a 500 means the endpoint really ran and the *database* was the thing
// that couldn't be reached - dbCheck's own try/catch is the only thing that produces one.
//
// The whole point is to stop blaming the database for problems that aren't the database's: a
// stale backend missing the route (404) and a backend that isn't up at all (no status) both used
// to surface as "Cannot access the database", which is exactly the wrong-turn this endpoint was
// added to prevent. Keep the database wording reserved for the 500 case.
function describeDbCheckFailure(err: unknown): string {
    const status = (err as { status?: number } | null)?.status;

    if (status === undefined)
        return "Cannot reach the backend API.\n\nIt may not be running, or VITE_API_URL may be pointing somewhere wrong.";
    if (status === 404)
        return "Cannot reach the backend API.\n\nThe /login/db_check endpoint is missing - the backend is probably running an older build than the frontend. Restart the backend.";
    if (status === 401 || status === 403)
        return "The backend API rejected your sign-in.\n\nYour session may have expired - try signing in again.";
    if (status === 500)
        return "Cannot access the database";
    return `Cannot reach the backend API (HTTP ${status}).`;
}

export const CurrentUserContext = createContext<CurrentUserContextType>({
    user: null,
    isAdmin: false,
    isCustomerAdmin: false,
    isConsultant: false,
    readAccess: false,
    customerType: "",
    portalOffline: false,
    scheduledResume: null,
    dbName: null,
});

interface CurrentUserContextType {
    user: UserType | null;
    isAdmin: boolean;
    isCustomerAdmin: boolean,
    isConsultant: boolean,
    readAccess: boolean,
    customerType: string,
    portalOffline: boolean;
    scheduledResume: Date | null;
    // Name of the database the backend is connected to, from the login DB health check.
    // Surfaced on the Technical Details page alongside the build/deploy details.
    dbName: string | null;
}

interface CurrentUserProviderProps {
    children: ReactNode;
}

export function CurrentUserProvider({ children } : CurrentUserProviderProps) {
    const {getCurrentUser, checkPermissions, checkDatabase, checkLoginFileAccess, ready: apiReady} = useApi();
    const { logout } = useAuth();
    const [user, setUser] = useState<UserType | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [readAccess, setReadAccess] = useState(false);
    const [isCustomerAdmin, setIsCustomerAdmin] = useState(false);
    const [isConsultant, setIsConsultant] = useState(false);
    const [customerType, setCustomerType] = useState("");
    const [portalOffline, setPortalOffline] = useState(false);
    const [scheduledResume, setScheduledResume] = useState<Date | null>(null);
    const [dbName, setDbName] = useState<string | null>(null);
    const [ready, setReady] = useState(false);
    
    async function fetchUser() {
        const userData = await getCurrentUser();
        setUser(userData);
        setIsAdmin(userData.admin === "1");
        setIsCustomerAdmin(userData.customeradmin === "1");
        //setReadAccess(userData.readaccess === "1");
        setIsConsultant(userData.consultant === "1");
        setCustomerType(userData.customerType)
        setPortalOffline(userData.portalOffline === "1");
        setScheduledResume(userData.scheduledResume ? new Date(userData.scheduledResume) : null);
    }

    async function checkUserPermissions() {
        const resp = await checkPermissions("LOGIN");
        setReadAccess(resp.has_access === 1);
        if (resp.has_access !== 1 && resp.message)
            alert (resp.message);
    }

    useEffect(() => {
        async function loadCurrentUser() {
            // Mandatory login checks (DB + every configured file share) - a failure in any of
            // them must block the user out entirely, not let them use the app against a database
            // or file share that isn't actually reachable. `ready` is deliberately never set to
            // true on failure, so the app body never renders (the spinner stays up until logout
            // fires).

            // 1. Connectivity only - a bare `select db_name()` on the backend, touching no
            // stored procedure or table (see app/src/controllers/Health.ts). This is the ONLY
            // check allowed to report "Cannot access the database", and only when the failure is
            // actually the database (a 500) rather than the API being unreachable - see
            // describeDbCheckFailure above.
            try {
                const { dbName } = await checkDatabase();
                setDbName(dbName);
            } catch (err) {
                console.error("Login database connectivity check failed:", err);
                alert(describeDbCheckFailure(err));
                logout();
                return;
            }

            // 2. The user's own record. The database is already proven reachable at this point,
            // so a failure here is NOT a connectivity problem - it's a missing/failing stored
            // procedure or a problem with this user's data. These used to share the catch block
            // above and so reported "Cannot access the database", which sent debugging in
            // completely the wrong direction when utl.sp_user_email_check simply wasn't deployed
            // to the target DB (see _DEVNOTES §2). Surface the real error instead of guessing.
            try {
                await fetchUser();
                await checkUserPermissions();
            } catch (err) {
                console.error("Login user check failed:", err);
                const detail = err instanceof Error ? err.message : String(err);
                alert(`Connected to the database, but your user account could not be loaded.\n\n${detail}`);
                logout();
                return;
            }

            try {
                const { results } = await checkLoginFileAccess();
                const failedDrives = results.filter((r) => !r.ok).map((r) => r.drive);
                if (failedDrives.length > 0) {
                    for (const drive of failedDrives) {
                        alert(`Cannot access the ${drive} drive`);
                    }
                    logout();
                    return;
                }
            } catch (err) {
                // The file-check endpoint itself failed to respond at all (as opposed to
                // responding with a specific drive's ok:false) - can't name a specific drive here.
                console.error("Login file-share check failed:", err);
                alert("Cannot access the file share");
                logout();
                return;
            }

            setReady(true);
        }

        if (apiReady)
            loadCurrentUser();
    }, [apiReady]);

    if (!ready) {
        // Was a bare, unwrapped <Spinner/> - renders at its default top-left document-flow
        // position with no centering, no background, no text (pre-existing, unrelated to the
        // MSAL loading gate above). Harmless when it was the only loading indicator in the app,
        // but jarring once a proper splash precedes it - swapped for the same LoadingSplash so
        // the whole load (pre-JS -> MSAL -> this current-user fetch) reads as one continuous
        // splash with no visual seams, not three different-looking loading states in a row.
        return <LoadingSplash />;
    }

    return (
        <CurrentUserContext.Provider value={{ user, isAdmin, isCustomerAdmin, readAccess, customerType, isConsultant, portalOffline, scheduledResume, dbName }}>
            {children}
        </CurrentUserContext.Provider>
    );
}